import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { TbLayoutGrid } from "react-icons/tb";
import { PageSeo } from "../seo";
import { L } from "../../../../constants/baysportLabels";
import { TRIAL_DAYS } from "../../../../constants/trial";
import { PATHS } from "../nav";
import site from "../site.module.css";
import h from "./home.module.css";
import { PageHero, PhonesArt, RegisterSection, Title } from "./parts";

const ALL = "Во всех тарифах";
const CLUB_PRO = "«Клуб» и «Про»";

const BLOCKS = [
  {
    id: "schedule",
    nav: "Календарь",
    title: ["Расписание групп", "и тренеров"],
    text: "Группы, персональные занятия, залы и тренеры — в одном календаре. Перенесли занятие — клиенты сразу получают уведомление.",
    points: ["Группы и персональные", "Залы и тренеры", "Уведомление о переносе"],
    plan: ALL,
    screen: "mock-owner-calendar.png",
  },
  {
    id: "journal",
    nav: "Журнал",
    title: ["Журнал посещений", "вместо тетради"],
    text: "Открываете занятие и отмечаете, кто пришёл. В строке ученика видно, есть ли абонемент: занятие списывается само, история остаётся в карточке.",
    points: ["Отметка группы за минуту", "Списание с абонемента", "История посещений"],
    plan: ALL,
    screen: "mock-owner-journal.png",
  },
  {
    id: "memberships",
    nav: "Абонементы",
    title: ["Абонементы", "по занятиям или сроку"],
    text: "Пакет занятий или срок действия, с заморозкой. Разовое занятие — тоже. На главной у клиента крупно видно, сколько занятий осталось.",
    points: ["По занятиям или по сроку", "Заморозка и разовые", "Остаток на главной клиента"],
    plan: ALL,
    screen: "mock-client-home.png",
  },
  {
    id: "finance",
    nav: "Финансы",
    title: ["Финансы клуба", "и ваш банк"],
    text: "Наличные и переводы собираются в одном журнале: кто оплатил и кто должен. Есть возможность подключить эквайринг ЮKassa или Т-Банка.",
    points: ["Наличные и переводы", "Долги клиентов", "ЮKassa и Т-Банк — «Клуб» и «Про»"],
    plan: "Учёт — во всех, банк — «Клуб» и «Про»",
    screen: "mock-owner-finance.png",
  },
  {
    id: "clients",
    nav: "Ученики",
    title: ["База учеников", "и родителей"],
    text: "У ученика и родителя одна карточка: контакты, абонемент и посещения. Список из Excel можно загрузить, новых пригласить ссылкой или QR-кодом.",
    points: ["Контакты родителей", "Загрузка из Excel", "Приглашение ссылкой или QR"],
    plan: ALL,
    screen: "mock-owner-clients.png",
  },
  {
    id: "client",
    nav: "Кабинет клиента",
    tab: "Клиент",
    title: ["Кабинет", "клиента"],
    text: "Ученик или родитель открывает свою главную. Сразу видно, сколько занятий осталось, сколько раз уже был в этом месяце и когда следующая тренировка — с залом и тренером. Тут же можно отметить, придёт ли на занятие, и открыть своё расписание.",
    points: ["Остаток занятий и посещения", "Ближайшая тренировка, зал и тренер", "Отметка «приду» или «не приду»", "Своё расписание"],
    plan: `${CLUB_PRO} · или модулем`,
    screen: "mock-client-home.png",
  },
  {
    id: "trainer",
    nav: "Кабинет тренера",
    tab: "Тренер",
    title: ["Кабинет тренера", "со своими группами"],
    text: "Тренер видит только свои группы, расписание и журнал. Касса и настройки клуба остаются у владельца.",
    points: ["Свои группы и расписание", "Журнал своих занятий", "Без кассы клуба"],
    plan: `${CLUB_PRO} · или модулем`,
    screen: "mock-owner-home.png",
  },
  {
    id: "chat",
    nav: "Чат и опросы",
    tab: "Чат",
    title: ["Чат, push", "и опросы"],
    text: "Переписка с клубом идёт в приложении, без сторонних мессенджеров. О переносе приходит push. Опросом можно спросить группу, кто придёт.",
    points: ["Чат клуба", "Push о переносах и новостях", "Опросы — «Клуб» и «Про»"],
    plan: "Чат и push — во всех, опросы — «Клуб» и «Про»",
    screen: "mock-client-chat.png",
  },
  {
    id: "pwa",
    nav: "Приложение",
    title: ["Приложение клуба", "на главный экран"],
    text: "Приложение открывается по ссылке и ставится на главный экран телефона — без App Store и Google Play. При входе открывается экран клуба.",
    points: ["Ссылка или QR, без магазинов", "Иконка на главном экране", "Экран клуба при входе"],
    plan: `${CLUB_PRO} · или модулем`,
    screen: "mock-brand-splash.png",
  },
  {
    id: "social",
    nav: "Соцсеть",
    title: ["Соцсеть участников", "и лента клуба"],
    text: "Соцсеть внутри клуба: профили участников, подписки и общая лента. Туда попадают новости и фото клуба, записи со стен и достижения.",
    points: ["Профили участников", "Подписки, стена и достижения", "Лента: новости, фото и записи"],
    plan: "«Про» · или модулем",
    screen: "mock-client-profile.png",
  },
  {
    id: "branding",
    nav: "Брендинг",
    title: ["Клуб", "под вашим брендом"],
    text: "Клиент видит свой клуб: логотип, обложку и цвет. Логотип входит в «Клуб». Свой экран при входе и оформление без Бай.Спорт — в «Про» или модулем «Брендинг».",
    points: ["Логотип, обложка и цвет", "Свой экран при входе", "Без Бай.Спорт в «Про»"],
    plan: "Логотип — «Клуб», полное — «Про»",
    screen: "mock-brand-club.png",
  },
];

export default function FeaturesPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash]);

  return (
    <>
      <PageSeo page="features" />

      <PageHero
        pill="Возможности CRM для спортивного клуба"
        lines={["Всё для клуба.", "В одном сервисе."]}
        lead="Календарь, журнал, абонементы и финансы. Кабинеты клиента и тренера, чат и опросы, приложение на телефон, соцсеть и брендинг."
        actions={
          <>
            <a href="#register" className={`${site.btn} ${site.btnLg}`}>
              Создать клуб бесплатно
            </a>
            <Link to={PATHS.pricing} className={h.link}>
              Тарифы и модули ↗
            </Link>
          </>
        }
        note={`${TRIAL_DAYS} дней полного доступа • без привязки карты`}
        visual={
          <PhonesArt
            icon={TbLayoutGrid}
            screens={["mock-owner-calendar.png", "mock-client-home.png"]}
          />
        }
      />

      <div className={h.anchorBar}>
        <nav className={`${h.wrap} ${h.anchorNav}`} aria-label="Разделы">
          {BLOCKS.map((b) => (
            <a key={b.id} href={`#${b.id}`}>
              {b.tab || b.nav}
            </a>
          ))}
        </nav>
      </div>

      {BLOCKS.map((b, i) => (
        <section
          key={b.id}
          id={b.id}
          className={`${h.section} ${h.featSection} ${i % 2 ? h.soft : ""}`}
        >
          <div className={`${h.wrap} ${h.split} ${i % 2 ? h.splitFlip : ""}`}>
            <div className={`${h.splitArt} ${b.shots ? h.splitArtPair : ""}`}>
              {(b.shots || [{ src: b.screen }]).map((shot) => (
                <figure key={shot.src} className={h.shot}>
                  {shot.label ? <figcaption>{shot.label}</figcaption> : null}
                  <img
                    src={`/landing/${shot.src}`}
                    alt={
                      shot.label
                        ? `${shot.label}: ${b.nav} — экран ${L.productName}`
                        : `${b.nav} — экран ${L.productName}`
                    }
                    loading="lazy"
                  />
                </figure>
              ))}
            </div>
            <div>
              <p className={h.kicker}>
                {String(i + 1).padStart(2, "0")} / {b.nav}
              </p>
              <Title lines={b.title} />
              <p className={h.sub}>{b.text}</p>
              <ul className={h.featList}>
                {b.points.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
              <span className={h.planTag}>{b.plan}</span>
            </div>
          </div>
        </section>
      ))}

      <section className={h.section}>
        <div className={h.wrap}>
          <div className={h.ask}>
            <div>
              <h3>Какие возможности входят в ваш тариф?</h3>
              <p>
                Сравните «Старт», «Клуб» и «Про» и посчитайте стоимость с
                модулями.
              </p>
            </div>
            <Link to={PATHS.pricing} className={site.btn}>
              Тарифы и модули
            </Link>
          </div>
        </div>
      </section>

      <RegisterSection
        kicker="Регистрация"
        title={["Проверьте всё это", "на своём клубе"]}
        soft
      />
    </>
  );
}
